import { createJSONStorage, loadable } from 'jotai/utils'
import { Atom, useAtomValue } from 'jotai'
import { atomWithStorage, atomFamily as jotaiAtomFamily } from 'jotai/utils'
import deepEqual from 'fast-deep-equal'
import { browserLogger } from 'src/utils/browser-logger'
import { useRef } from 'react'

const storage = createJSONStorage<any>(() => localStorage)
export const atomWithLocalStorage = <Value>(key: string, initialValue: Value) =>
  atomWithStorage<Value>(key, initialValue, storage)

export const useAtomValueLoadable = <Value>(anAtom: Atom<Value>) => useAtomValue(loadable(anAtom))

export const useCachedAtomValue = <Value>(anAtom: Atom<Value>, defaultValue: Awaited<Value>): Awaited<Value> => {
  const loadableValue = useAtomValueLoadable(anAtom)
  const valueRef = useRef<Awaited<Value>>(defaultValue)

  if (loadableValue.state === 'hasError') {
    const msg = `jotai error for ${anAtom.debugLabel}:`
    browserLogger.error(msg, loadableValue.error)
    throw loadableValue.error
  }

  if (loadableValue.state === 'hasData') {
    const value = loadableValue.data
    if (value) {
      valueRef.current = value
    } else {
      valueRef.current = defaultValue
    }
  }

  return valueRef.current
}

/**
 * Use this instead of atomFamily from jotai/utils for deep equality check for params. This is the same behavior as Recoil's atomFamily/selectorFamily.
 * https://jotai.org/docs/utilities/family#usage
 */
export const atomFamily = <Param, AtomType extends Atom<unknown>>(initializeAtom: (param: Param) => AtomType) =>
  jotaiAtomFamily(initializeAtom, deepEqual)
